/* eslint-disable */
import { getLiveOrders, getLiveOrderCounts } from '@/api/liveOrders'
import { play as playAlert, reset as resetSound } from '@/services/notificationSound'
import statuses from '@/assets/static/statuses.json'

/**
 * The platform-wide live order queue (ADMIN / AGENT / MANAGER).
 *
 * Fed by the orders:v1:platform Ably channel. Only four transitions are ever published:
 *   7 UNASSIGNED          -> a new order nobody owns yet
 *   1 ASSIGNED_TO_BRANCH  -> pushed to a branch, still ours
 *   2 ACCEPTED_BY_BRANCH  -> leaves the queue, now the restaurant's problem
 *   9 CANCELED            -> leaves the queue
 */

const STATUS_UNASSIGNED = 7
const STATUS_ASSIGNED_TO_BRANCH = 1
const STATUS_ACCEPTED_BY_BRANCH = 2
const STATUS_CANCELED = 9

const RELOAD_DEBOUNCE_MS = 1500
const FLASH_MS = 2600
const SEEN_LIMIT = 500

// Module scope, not state: a Set is not reactive in Vue 2, and 500 ids have no business
// showing up in every devtools snapshot.
const seenEventIds = new Set()
let reloadTimer = null
let countsTimer = null

// Orders announced before their row has been fetched. The row is flashed once the next
// refresh actually delivers it, so the highlight cannot expire while we are still waiting.
const pendingFlashIds = []

/**
 * The realtime payload carries status_code and status_name but no css_class, so the label
 * colour has to come from the shared statuses map. Keyed on status_code: status_name is a
 * DB display string and can change.
 *
 * The backend emits UNASSIGNED while statuses.json keys that status UN_ASSIGNED, so the
 * two vocabularies need reconciling — without this, status 7 silently resolves to nothing.
 */
const STATUS_CODE_ALIASES = {
    UNASSIGNED: 'UN_ASSIGNED'
}

const statusPresentation = (statusCode) => {
    if (!statusCode) return null
    const key = STATUS_CODE_ALIASES[statusCode] || statusCode
    const match = statuses[key]
    return match ? { name: match.name, cssClass: match.cssClass } : null
}

const emptyCounts = () => ({
    unassigned: 0,
    assignedToBranch: 0,
    liveTotal: 0,
    createdLastHour: 0,
    oldestUnassignedAt: null,
    oldestLiveAt: null,
    asOf: null
})

const mapCounts = (raw) => {
    if (!raw) return emptyCounts()
    return {
        unassigned: raw.unassigned || 0,
        assignedToBranch: raw.assigned_to_branch || 0,
        liveTotal: raw.live_total || 0,
        createdLastHour: raw.created_last_hour || 0,
        oldestUnassignedAt: raw.oldest_unassigned_at || null,
        oldestLiveAt: raw.oldest_live_at || null,
        asOf: raw.as_of || null
    }
}

const mapRow = (raw) => ({
    orderId: raw.order_id,
    restaurantId: raw.restaurant_id,
    restaurantName: raw.restaurant_name,
    branchId: raw.branch_id,
    branchName: raw.branch_name,
    customerName: raw.customer_name,
    baykedCustomerName: raw.bayked_customer_name,
    areaName: raw.area_name,
    cityName: raw.city_name,
    orderTypesId: raw.order_type_id,
    orderTypeName: raw.order_type_name,
    orderSourceName: raw.order_source_name,
    billedPrice: raw.billed_price,
    orderCreated: raw.order_created,
    orderModified: raw.order_modified,
    tableName: raw.table_name,
    paymentMethodName: raw.payment_method_name,
    paymentStatusName: raw.payment_status_name,
    agentName: raw.agent_name,
    statusId: raw.status_id,
    statusName: raw.status_name,
    cssClass: raw.css_class,
    complainCount: raw.complain_count
})

export default {
    namespaced: true,

    state: () => ({
        rows: [],
        page: 0,
        size: 25,
        totalRows: 0,
        totalPages: 0,
        counts: emptyCounts(),
        filters: { statusId: null, restaurantId: null, orderId: null },
        sort: 'newest',
        loading: false,
        // initialized | connected | disconnected | suspended | failed
        connection: 'initialized',
        // BRANCH | PLATFORM | null (403: this role gets no realtime)
        scope: null,
        flashIds: [],
        moveIds: [],
        focusOrderId: null,
        newSinceLook: 0,
        lastEventAt: null,
        soundEnabled: JSON.parse(localStorage.getItem('liveOrdersSound') || 'true'),
        toasts: [],
        // True only while the live page is mounted AND its tab is in the foreground.
        // Set by views/LiveOrders.vue; drives the silent-while-watching rule.
        livePageVisible: false,
        // True whenever the live page is mounted, foreground or not. Decides whether an
        // event needs the full table or just the counts behind the sidebar badge.
        livePageMounted: false
    }),

    getters: {
        // The sidebar badge. Drains as branches accept and orders get cancelled.
        pendingCount: state => state.counts.liveTotal,
        isPlatform: state => state.scope === 'PLATFORM',
        connectionLabel: state => {
            switch (state.connection) {
                case 'connected': return 'Live'
                case 'disconnected': return 'Reconnecting'
                case 'suspended': return 'Offline'
                case 'failed': return 'Realtime failed'
                default: return 'Connecting'
            }
        },
        unassignedPct: state => {
            const total = state.counts.liveTotal
            return total ? Math.round((state.counts.unassigned / total) * 100) : 0
        },
        // 12 five-minute buckets derived from rows already in hand, so the arrivals chart
        // costs no extra request and no MySQL date maths.
        arrivalBuckets: state => {
            const buckets = new Array(12).fill(0)
            const now = Date.now()
            state.rows.forEach(row => {
                if (!row.orderCreated) return
                const minutesAgo = (now - new Date(row.orderCreated).getTime()) / 60000
                if (minutesAgo < 0 || minutesAgo >= 60) return
                const index = 11 - Math.floor(minutesAgo / 5)
                if (index >= 0 && index < 12) buckets[index] += 1
            })
            return buckets
        }
    },

    mutations: {
        setLoading (state, value) { state.loading = value },
        setConnection (state, value) { state.connection = value },
        setScope (state, value) { state.scope = value },
        setPage (state, value) { state.page = value },
        setSize (state, value) { state.size = value; state.page = 0 },
        setSort (state, value) { state.sort = value; state.page = 0 },
        setFilters (state, filters) {
            state.filters = Object.assign({}, state.filters, filters)
            state.page = 0
        },
        setPageData (state, payload) {
            state.rows = (payload.rows || []).map(mapRow)
            state.page = payload.page
            state.size = payload.size
            state.totalRows = payload.total_rows
            state.totalPages = payload.total_pages
            state.counts = mapCounts(payload.counts)
        },
        setCounts (state, raw) { state.counts = mapCounts(raw) },
        setLastEventAt (state, value) { state.lastEventAt = value },
        setFocusOrderId (state, value) { state.focusOrderId = value },
        setLivePageVisible (state, value) { state.livePageVisible = value },
        setLivePageMounted (state, value) { state.livePageMounted = value },

        removeRow (state, orderId) {
            state.rows = state.rows.filter(row => row.orderId !== Number(orderId))
        },

        markAssigned (state, payload) {
            const row = state.rows.find(r => r.orderId === Number(payload.order_id))
            if (!row) return
            const presentation = statusPresentation(payload.status_code)
            row.statusId = payload.status_id
            row.statusName = presentation ? presentation.name : payload.status_name
            // Without this the row keeps the amber UN-ASSIGNED label after being assigned.
            if (presentation) row.cssClass = presentation.cssClass
        },

        /**
         * Optimistic count maths so the tiles and the sidebar badge move the instant an
         * event lands, with no round trip. Any drift is corrected by the debounced
         * refresh, the reconnect resync and the periodic counts poll.
         */
        adjustCounts (state, { from, to }) {
            const counts = state.counts
            const bump = (status, delta) => {
                if (status === STATUS_UNASSIGNED) {
                    counts.unassigned = Math.max(0, counts.unassigned + delta)
                } else if (status === STATUS_ASSIGNED_TO_BRANCH) {
                    counts.assignedToBranch = Math.max(0, counts.assignedToBranch + delta)
                }
            }
            bump(from, -1)
            bump(to, 1)
            counts.liveTotal = counts.unassigned + counts.assignedToBranch
            if (to === STATUS_UNASSIGNED) counts.createdLastHour += 1
        },

        flash (state, { orderId, kind }) {
            const list = kind === 'move' ? state.moveIds : state.flashIds
            if (!list.includes(orderId)) list.push(orderId)
        },
        unflash (state, { orderId, kind }) {
            if (kind === 'move') state.moveIds = state.moveIds.filter(id => id !== orderId)
            else state.flashIds = state.flashIds.filter(id => id !== orderId)
        },

        bumpNewSinceLook (state) { state.newSinceLook += 1 },
        acknowledgeNew (state) { state.newSinceLook = 0 },

        toggleSound (state) {
            state.soundEnabled = !state.soundEnabled
            localStorage.setItem('liveOrdersSound', JSON.stringify(state.soundEnabled))
        },

        pushToast (state, toast) {
            state.toasts.push(toast)
            if (state.toasts.length > 5) state.toasts.shift()
        },
        dismissToast (state, id) {
            state.toasts = state.toasts.filter(t => t.id !== id)
        },

        reset (state) {
            state.rows = []
            state.page = 0
            state.totalRows = 0
            state.totalPages = 0
            state.counts = emptyCounts()
            state.filters = { statusId: null, restaurantId: null, orderId: null }
            state.connection = 'initialized'
            state.scope = null
            state.flashIds = []
            state.moveIds = []
            state.focusOrderId = null
            state.newSinceLook = 0
            state.lastEventAt = null
            state.toasts = []
            state.livePageVisible = false
            state.livePageMounted = false
        }
    },

    actions: {
        /**
         * Re-runs the current query. Page, sort and filters are preserved unless the
         * caller overrides them, so a background refresh can never yank an operator back
         * to page 1 while they are working.
         */
        async refresh ({ state, commit, dispatch }, overrides = {}) {
            commit('setLoading', true)
            try {
                const params = {
                    page: overrides.page !== undefined ? overrides.page : state.page,
                    size: overrides.size !== undefined ? overrides.size : state.size,
                    sort: overrides.sort !== undefined ? overrides.sort : state.sort
                }
                const filters = Object.assign({}, state.filters, overrides.filters || {})
                if (filters.statusId !== null) params.status_id = filters.statusId
                if (filters.restaurantId !== null) params.restaurant_id = filters.restaurantId
                if (filters.orderId !== null) params.order_id = filters.orderId

                const { data } = await getLiveOrders(params)
                commit('setPageData', data)
                dispatch('drainPendingFlashes')
            } catch (error) {
                // 403 means this role gets no live queue. Silent, not an error.
                if (!(error.response && error.response.status === 403)) {
                    console.warn('[LiveOrders] refresh failed', error)
                }
            } finally {
                commit('setLoading', false)
            }
        },

        async refreshCounts ({ commit }) {
            try {
                const { data } = await getLiveOrderCounts()
                commit('setCounts', data)
            } catch (error) {
                if (!(error.response && error.response.status === 403)) {
                    console.warn('[LiveOrders] counts failed', error)
                }
            }
        },

        // A busy dinner service fires many events per second; collapse them into one request.
        scheduleRefresh ({ dispatch }) {
            if (reloadTimer) clearTimeout(reloadTimer)
            reloadTimer = setTimeout(() => {
                reloadTimer = null
                dispatch('refresh')
            }, RELOAD_DEBOUNCE_MS)
        },

        scheduleCountsRefresh ({ dispatch }) {
            if (countsTimer) clearTimeout(countsTimer)
            countsTimer = setTimeout(() => {
                countsTimer = null
                dispatch('refreshCounts')
            }, RELOAD_DEBOUNCE_MS)
        },

        /**
         * The only thing that pulls data after the initial load. There is no polling:
         * every fetch below is caused by an Ably event or by a reconnect.
         *
         * Off the live page the only thing that can change is the sidebar badge, so just
         * the counts are fetched. On the live page the page request already carries counts
         * in its response, so asking for both would be a wasted round trip per event.
         */
        syncAfterEvent ({ state, dispatch }) {
            if (state.livePageMounted) dispatch('scheduleRefresh')
            else dispatch('scheduleCountsRefresh')
        },

        // Delivery is best-effort with no replay, so close the gap left by a network blip.
        resyncAfterReconnect ({ state, dispatch }) {
            if (state.livePageMounted) dispatch('refresh')
            else dispatch('refreshCounts')
        },

        /** Flash any announced order whose row has now arrived. */
        drainPendingFlashes ({ state, dispatch }) {
            if (pendingFlashIds.length === 0) return
            const present = pendingFlashIds.filter(id => state.rows.some(row => row.orderId === id))
            present.forEach(id => {
                pendingFlashIds.splice(pendingFlashIds.indexOf(id), 1)
                dispatch('flashFor', { orderId: id, kind: 'new' })
            })
            // Anything still unresolved is on another page; don't keep it queued forever.
            if (pendingFlashIds.length > 50) pendingFlashIds.splice(0, pendingFlashIds.length - 50)
        },

        /**
         * Highlight this order as soon as it is on screen. Used when an operator arrives
         * from a toast click: the row is usually not fetched yet, so it is queued rather
         * than filtering the queue down to that one order and hiding everything else.
         */
        requestFlash ({ state, dispatch }, orderId) {
            const id = Number(orderId)
            if (!id) return
            if (!pendingFlashIds.includes(id)) pendingFlashIds.push(id)
            dispatch('drainPendingFlashes')
        },

        flashFor ({ commit }, { orderId, kind }) {
            commit('flash', { orderId, kind })
            setTimeout(() => commit('unflash', { orderId, kind }), FLASH_MS)
        },

        handlePlatformEvent ({ state, commit, dispatch }, payload) {
            if (!payload || !payload.event_id) return

            // The backend has no "already sent" guard: a retried transition or a duplicate
            // payment webhook republishes the same event_id. Client dedupe is what makes
            // this correct.
            if (seenEventIds.has(payload.event_id)) return
            seenEventIds.add(payload.event_id)
            if (seenEventIds.size > SEEN_LIMIT) {
                seenEventIds.delete(seenEventIds.values().next().value)
            }

            commit('setLastEventAt', payload.occurred_at || new Date().toISOString())
            const orderId = Number(payload.order_id)

            switch (payload.status_id) {
                case STATUS_ACCEPTED_BY_BRANCH:
                case STATUS_CANCELED:
                    // Out of the platform's court. Drop the row with no refetch, then
                    // backfill the page quietly.
                    commit('removeRow', orderId)
                    commit('adjustCounts', { from: payload.previous_status_id, to: null })
                    dispatch('syncAfterEvent')
                    break

                case STATUS_ASSIGNED_TO_BRANCH:
                    commit('adjustCounts', {
                        from: STATUS_UNASSIGNED, to: STATUS_ASSIGNED_TO_BRANCH
                    })
                    if (state.rows.some(row => row.orderId === orderId)) {
                        commit('markAssigned', payload)
                        dispatch('flashFor', { orderId, kind: 'move' })
                    } else {
                        dispatch('syncAfterEvent')
                    }
                    break

                case STATUS_UNASSIGNED:
                    commit('adjustCounts', { from: null, to: STATUS_UNASSIGNED })
                    commit('bumpNewSinceLook')
                    // The event carries no customer, amount or payment detail by design,
                    // so the row itself has to come from the REST queue.
                    if (!pendingFlashIds.includes(orderId)) pendingFlashIds.push(orderId)
                    dispatch('syncAfterEvent')
                    dispatch('announce', payload)
                    break
            }
        },

        /**
         * Decides whether a new order interrupts.
         *
         * Silent while the operator is actually looking at the live page: the row flashes
         * in, the tiles tick and the "new since you looked" pill counts it. A toast there
         * would sit on top of the table's own controls and have to be dismissed by hand
         * during exactly the rush it is reporting. A backgrounded tab means nobody is
         * looking, so it rings.
         */
        announce ({ state, commit }, payload) {
            if (state.livePageVisible) return

            if (state.soundEnabled) playAlert(payload.event_id)

            commit('pushToast', {
                id: payload.event_id,
                orderId: payload.order_id,
                restaurantName: payload.restaurant_name,
                branchArea: payload.branch_area,
                notificationKey: payload.notification_key
            })
        },

        reset ({ commit }) {
            if (reloadTimer) { clearTimeout(reloadTimer); reloadTimer = null }
            if (countsTimer) { clearTimeout(countsTimer); countsTimer = null }
            seenEventIds.clear()
            pendingFlashIds.length = 0
            resetSound()
            commit('reset')
        }
    }
}
